import { X, Sparkles, Check } from "lucide-react";
import { BLOCK_TEMPLATES, THEME_PRESETS, expandTemplate } from "@/lib/templateSets";

/** Modal that lets a user (super-admin or salon-admin) insert one of the
 *  pre-built BLOCK_TEMPLATES into their current block list. */
export function TemplatePicker({ open, onClose, onInsert }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="template-picker">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="mv-label mb-1">Vorlagen-Sets</p>
            <h2 className="font-display text-2xl">Fertige Block-Komposition einfügen</h2>
            <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
              Klick auf eine Vorlage — die enthaltenen Blöcke werden ans Ende deiner Liste angehängt. Du kannst sie danach beliebig anpassen.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BLOCK_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              data-testid={`template-${tpl.id}`}
              onClick={() => { onInsert(expandTemplate(tpl)); onClose(); }}
              className="text-left p-4 rounded-xl border hover:shadow-lg transition-shadow"
              style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }}
            >
              <p className="text-[11px] mb-1.5" style={{ color: "var(--mv-text-muted)" }}>{tpl.category}</p>
              <p className="font-medium mb-1">{tpl.name}</p>
              <p className="text-xs leading-relaxed" style={{ color: "var(--mv-text-muted)" }}>{tpl.description}</p>
              <p className="text-[10px] mt-2 font-mono" style={{ color: "var(--mv-text-muted)" }}>
                {expandTemplate(tpl).length} Block{expandTemplate(tpl).length === 1 ? "" : "s"}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Modal showing Wordpress-style theme presets that a salon-admin can apply. */
export function ThemePicker({ open, onClose, onApply, currentTheme }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-5xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="theme-picker">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="mv-label mb-1">Theme-Bibliothek</p>
            <h2 className="font-display text-2xl">Wähle ein fertiges Design</h2>
            <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
              Übernimmt Farben, Schriftarten und Button-Stil. Deine Inhalte (Logo, Texte, Bilder) bleiben unverändert.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {THEME_PRESETS.map((t) => {
            const isActive = currentTheme && currentTheme.primary_color === t.theme.primary_color && currentTheme.heading_font === t.theme.heading_font;
            return (
              <div key={t.id} data-testid={`theme-${t.id}`} className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--mv-border)" }}>
                <div className="relative h-44">
                  <img src={t.preview_image} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-end p-4" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.65) 100%)" }}>
                    <div>
                      <p className="text-xs font-mono" style={{ color: "rgba(255,255,255,0.8)" }}>{t.id}</p>
                      <p className="text-white font-display text-2xl">{t.name}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-3" style={{ background: t.theme.bg_color, color: t.theme.text_color, fontFamily: `'${t.theme.body_font}', system-ui` }}>
                  <p className="text-sm" style={{ fontFamily: `'${t.theme.heading_font}', serif`, fontSize: 22 }}>{t.name}</p>
                  <p className="text-xs leading-relaxed">{t.tagline}</p>
                  <div className="flex gap-2 pt-2">
                    {[t.theme.primary_color, t.theme.secondary_color, t.theme.text_color, t.theme.bg_color].map((c, i) => (
                      <span key={i} className="w-5 h-5 rounded-full border" style={{ background: c, borderColor: "rgba(0,0,0,0.1)" }} />
                    ))}
                  </div>
                  <button
                    data-testid={`apply-${t.id}`}
                    onClick={() => { onApply(t); onClose(); }}
                    className="w-full px-4 py-2 text-sm font-medium"
                    style={{ background: t.theme.primary_color, color: "#fff", borderRadius: t.theme.button_radius === "rounded-none" ? 0 : t.theme.button_radius === "rounded-lg" ? 8 : 999 }}
                  >
                    {isActive ? <><Check className="w-3.5 h-3.5 inline mr-1" /> Aktiv</> : "Dieses Theme übernehmen"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
